import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, Input, DatePicker, Select, message, Modal, Tooltip, ConfigProvider, Card, Row, Col, Statistic, Typography, Spin } from 'antd';
import { EyeOutlined, FilePdfOutlined, ReloadOutlined, SearchOutlined, ShopOutlined, FileTextOutlined, ExclamationCircleOutlined, ShoppingCartOutlined, UnorderedListOutlined, CloseOutlined, CloseSquareOutlined, CreditCardOutlined } from '@ant-design/icons';
import axios from 'axios';
import { NODE_INVOICE_API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { isAdminRole } from '../permissions';
import { useCart } from '../context/CartContext';
import { createCreditNote, getCreditNote } from '../services/creditNoteService';
import { getInvoice } from '../services/invoiceService';
import { analyticsService } from '../services/analyticsService';
import PartialCreditModal from '../components/invoices/PartialCreditModal';
import locale from 'antd/es/locale/es_ES';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import './invoicesPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;

const InvoicesPage = () => {
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const { clearCart, toggleItemInCart, updateItemQuantity, updateItemSplitter, toggleVolumePrice } = useCart();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // Summary state
    const [summary, setSummary] = useState({
        totalCount: 0,
        countByType: {
            facturaA: 0,
            facturaB: 0,
            facturaC: 0,
            presupuesto: 0
        },
        countByStatus: {
            approved: 0,
            canceled: 0,
            failed: 0,
            presupuesto: 0
        },
        currentMonth: {
            totalSum: 0,
            count: 0,
            approvedCount: 0,
            presupuestoCount: 0,
            countByType: {
                facturaA: 0,
                facturaB: 0,
                facturaC: 0,
                facturaE: 0,
                facturaM: 0,
                presupuesto: 0
            },
            canceledCount: 0,
            fullCanceledCount: 0,
            partialCanceledCount: 0,
            deductions: {
                canceled: 0,
                failed: 0,
                creditNoted: 0
            }
        }
    });
    // Track invoices that have approved credit notes
    const [invoicesWithCreditNotes, setInvoicesWithCreditNotes] = useState(new Set());
    // Track invoices with full credit notes (completely cancelled)
    const [invoicesWithFullCreditNotes, setInvoicesWithFullCreditNotes] = useState(new Set());
    // Track invoices with partial credit notes
    const [invoicesWithPartialCreditNotes, setInvoicesWithPartialCreditNotes] = useState(new Set());
    // Track mapping from invoice ID to credit note IDs for linking
    const [invoiceToCreditNotesMap, setInvoiceToCreditNotesMap] = useState({});
    // Track dates with invoices for disabling empty dates in DatePicker
    const [datesWithInvoices, setDatesWithInvoices] = useState([]);
    // Partial credit modal state
    const [partialCreditModalVisible, setPartialCreditModalVisible] = useState(false);
    const [selectedInvoiceForPartialCredit, setSelectedInvoiceForPartialCredit] = useState(null);

    // Set dayjs to Spanish locale
    dayjs.locale('es');

    // Filters - using new parameter names from API v3.0
    const [filters, setFilters] = useState({
        status: null,
        dateFrom: null,
        dateTo: null,
        tipoComprobante: null,
        numeroComprobante: null,
        clienteRazonSocial: null,
    });

    // Load available dates with invoices from backend
    const loadAvailableDates = async () => {
        try {
            // Get actual dates with invoices from backend
            const response = await analyticsService.getInvoiceDates(365); // Last year
            const rawDates = response.data?.dates || [];
            
            // Convert ISO datetime strings to YYYY-MM-DD format
            // Use UTC to avoid timezone conversion issues
            const dates = rawDates.map(dateStr => {
                // Parse as UTC and extract just the date part to avoid timezone shifts
                const dateOnly = String(dateStr).split('T')[0];
                return dateOnly;
            });
            
            // Remove duplicates
            const uniqueDates = [...new Set(dates)];
            
            setDatesWithInvoices(uniqueDates);
            console.log('[InvoicesPage] Loaded available dates:', uniqueDates.length);
        } catch (err) {
            console.error('[InvoicesPage] Error loading available dates:', err);
            // Fallback: enable last 90 days
            const dates = [];
            const today = dayjs();
            for (let i = 0; i < 90; i++) {
                dates.push(today.subtract(i, 'day').format('YYYY-MM-DD'));
            }
            setDatesWithInvoices(dates);
        }
    };

    // Fetch invoices from backend
    const fetchInvoices = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            // Special handling for cancellation status filters (client-side only)
            const isCancellationFilter = filters.status === 'cancelacion_completa' || filters.status === 'cancelacion_parcial';
            
            // Build query parameters
            const params = {
                page: isCancellationFilter ? 1 : page,
                limit: isCancellationFilter ? 1000 : pageSize, // Fetch all invoices for cancellation filters
            };
            
            // Don't send cancellation filters to backend, they're client-side only
            if (filters.status && !isCancellationFilter) params.status = filters.status;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo) params.dateTo = filters.dateTo;
            // Use new parameter names from API v3.0
            if (filters.tipoComprobante) params.tipoComprobante = filters.tipoComprobante;
            if (filters.numeroComprobante) params.numeroComprobante = filters.numeroComprobante;
            if (filters.clienteRazonSocial) params.clienteRazonSocial = filters.clienteRazonSocial;

            const response = await axios.get(`${NODE_INVOICE_API_URL}/invoices`, { params });

            if (response.data && response.data.success) {
                // Fetch credit notes first to get the cancellation data
                const creditNoteData = await fetchCreditNotesForInvoices();
                
                let invoicesList = response.data.data.invoices;
                
                // Store summary data
                if (response.data.data.summary) {
                    setSummary(response.data.data.summary);
                }
                
                // Apply client-side filtering for cancellation statuses
                if (isCancellationFilter && creditNoteData) {
                    if (filters.status === 'cancelacion_completa') {
                        invoicesList = invoicesList.filter(inv => 
                            creditNoteData.invoiceIdsWithFullCreditNotes.has(inv.id.toString())
                        );
                    } else if (filters.status === 'cancelacion_parcial') {
                        invoicesList = invoicesList.filter(inv => 
                            creditNoteData.invoiceIdsWithPartialCreditNotes.has(inv.id.toString())
                        );
                    }
                    
                    // For client-side filtering with pagination
                    const startIndex = (page - 1) * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedInvoices = invoicesList.slice(startIndex, endIndex);
                    
                    setInvoices(paginatedInvoices);
                    setPagination({
                        current: page,
                        pageSize: pageSize,
                        total: invoicesList.length,
                    });
                } else {
                    setInvoices(invoicesList);
                    setPagination({
                        current: response.data.data.pagination.page,
                        pageSize: response.data.data.pagination.limit,
                        total: response.data.data.pagination.total,
                    });
                }
            } else {
                message.error('Error al cargar facturas');
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            message.error('Error al cargar facturas del servidor');
        } finally {
            setLoading(false);
        }
    };

    // Function to disable dates without invoices in the DatePicker
    const disabledDate = (current) => {
        if (!current) return false;
        // Disable future dates
        if (current.isAfter(dayjs(), 'day')) return true;
        // Disable dates without invoices (from backend)
        const dateStr = current.format('YYYY-MM-DD');
        return !datesWithInvoices.includes(dateStr);
    };

    // Fetch credit notes and build a set of invoice IDs that have approved credit notes
    // Returns the credit note data for immediate use in filtering
    const fetchCreditNotesForInvoices = async () => {
        try {
            // Fetch all credit notes (fetch multiple pages if needed)
            let allCreditNotes = [];
            let currentPage = 1;
            let totalPages = 1;
            
            // Fetch first page to get total pages
            const firstResponse = await axios.get(`${NODE_INVOICE_API_URL}/credit-notes`, { 
                params: { page: 1, limit: 100 } // Use limit 100 to minimize requests
            });

            if (firstResponse.data && firstResponse.data.success) {
                const creditNotes = firstResponse.data.data.creditNotes || [];
                allCreditNotes = [...creditNotes];
                totalPages = firstResponse.data.data.pagination?.totalPages || 1;
                
                // Fetch remaining pages if any
                for (let page = 2; page <= totalPages; page++) {
                    const response = await axios.get(`${NODE_INVOICE_API_URL}/credit-notes`, { 
                        params: { page, limit: 100 }
                    });
                    
                    if (response.data && response.data.success) {
                        const moreCreditNotes = response.data.data.creditNotes || [];
                        allCreditNotes = [...allCreditNotes, ...moreCreditNotes];
                    }
                }
                
                // Build Sets of invoice IDs that have credit notes
                const invoiceIdsWithCreditNotes = new Set();
                const invoiceIdsWithFullCreditNotes = new Set();
                const invoiceIdsWithPartialCreditNotes = new Set();
                const invoiceToCreditNotes = {};
                
                allCreditNotes.forEach(cn => {
                    if (cn.original_invoice_id && cn.status === 'approved') {
                        const invId = cn.original_invoice_id.toString();
                        invoiceIdsWithCreditNotes.add(invId);
                        
                        // Build mapping from invoice to credit notes
                        if (!invoiceToCreditNotes[invId]) {
                            invoiceToCreditNotes[invId] = [];
                        }
                        invoiceToCreditNotes[invId].push(cn);
                        
                        // Track invoices with FULL credit notes separately
                        if (cn.cancellation_type === 'full') {
                            invoiceIdsWithFullCreditNotes.add(invId);
                        }
                        // Track invoices with PARTIAL credit notes
                        else if (cn.cancellation_type === 'partial') {
                            invoiceIdsWithPartialCreditNotes.add(invId);
                        }
                    }
                });
                
                setInvoicesWithCreditNotes(invoiceIdsWithCreditNotes);
                setInvoicesWithFullCreditNotes(invoiceIdsWithFullCreditNotes);
                setInvoicesWithPartialCreditNotes(invoiceIdsWithPartialCreditNotes);
                setInvoiceToCreditNotesMap(invoiceToCreditNotes);
                
                // Return the data for immediate use
                return {
                    invoiceIdsWithCreditNotes,
                    invoiceIdsWithFullCreditNotes,
                    invoiceIdsWithPartialCreditNotes,
                    invoiceToCreditNotes
                };
            }
        } catch (error) {
            console.error('Error fetching credit notes:', error);
            // Don't show error message to user, just log it
        }
        
        return {
            invoiceIdsWithCreditNotes: new Set(),
            invoiceIdsWithFullCreditNotes: new Set(),
            invoiceIdsWithPartialCreditNotes: new Set(),
            invoiceToCreditNotes: {}
        };
    };

    useEffect(() => {
        loadAvailableDates(); // Load dates with invoices for the DatePicker
        fetchInvoices();
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        fetchInvoices(paginationConfig.current, paginationConfig.pageSize);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Handle filter change with auto-reload when cleared
    const handleFilterChangeWithReload = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        // If value is empty/null (filter cleared), reload data
        if (!value || value === '') {
            setTimeout(() => fetchInvoices(1, pagination.pageSize), 50);
        }
    };

    // Handle Enter key to apply filters
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        fetchInvoices(1, pagination.pageSize);
    };

    // Clear filters and reload with defaults
    const handleClearFilters = () => {
        const defaultFilters = {
            status: null,
            dateFrom: null,
            dateTo: null,
            tipoComprobante: null,
            numeroComprobante: null,
            clienteRazonSocial: null,
        };
        setFilters(defaultFilters);
        // Immediately fetch with no filters (pass empty filters directly)
        fetchInvoicesWithFilters(defaultFilters, 1, pagination.pageSize);
    };

    // Fetch invoices with specific filters (used by handleClearFilters)
    const fetchInvoicesWithFilters = async (filterOverrides, page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            // Check if it's a cancellation filter
            const isCancellationFilter = filterOverrides.status === 'cancelacion_completa' || filterOverrides.status === 'cancelacion_parcial';
            
            const params = {
                page: isCancellationFilter ? 1 : page,
                limit: isCancellationFilter ? 1000 : pageSize,
            };
            
            // Don't send cancellation filters to backend
            if (filterOverrides.status && !isCancellationFilter) params.status = filterOverrides.status;
            if (filterOverrides.dateFrom) params.dateFrom = filterOverrides.dateFrom;
            if (filterOverrides.dateTo) params.dateTo = filterOverrides.dateTo;
            // Use new parameter names from API v3.0
            if (filterOverrides.tipoComprobante) params.tipoComprobante = filterOverrides.tipoComprobante;
            if (filterOverrides.numeroComprobante) params.numeroComprobante = filterOverrides.numeroComprobante;
            if (filterOverrides.clienteRazonSocial) params.clienteRazonSocial = filterOverrides.clienteRazonSocial;

            const response = await axios.get(`${NODE_INVOICE_API_URL}/invoices`, { params });

            if (response.data && response.data.success) {
                // Fetch credit notes
                const creditNoteData = await fetchCreditNotesForInvoices();
                
                let invoicesList = response.data.data.invoices;
                
                // Store summary data
                if (response.data.data.summary) {
                    setSummary(response.data.data.summary);
                }
                
                // Apply client-side filtering for cancellation statuses
                if (isCancellationFilter && creditNoteData) {
                    if (filterOverrides.status === 'cancelacion_completa') {
                        invoicesList = invoicesList.filter(inv => 
                            creditNoteData.invoiceIdsWithFullCreditNotes.has(inv.id.toString())
                        );
                    } else if (filterOverrides.status === 'cancelacion_parcial') {
                        invoicesList = invoicesList.filter(inv => 
                            creditNoteData.invoiceIdsWithPartialCreditNotes.has(inv.id.toString())
                        );
                    }
                    
                    const startIndex = (page - 1) * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedInvoices = invoicesList.slice(startIndex, endIndex);
                    
                    setInvoices(paginatedInvoices);
                    setPagination({
                        current: page,
                        pageSize: pageSize,
                        total: invoicesList.length,
                    });
                } else {
                    setInvoices(invoicesList);
                    setPagination({
                        current: response.data.data.pagination.page,
                        pageSize: response.data.data.pagination.limit,
                        total: response.data.data.pagination.total,
                    });
                }
            } else {
                message.error('Error al cargar facturas');
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            message.error('Error al cargar facturas del servidor');
        } finally {
            setLoading(false);
        }
    };

    // View invoice details
    const handleViewInvoice = (invoiceId) => {
        window.open(`#/print-quote?invoiceId=${invoiceId}`, '_blank');
    };

    // Load invoice items to cart
    const handleLoadToCart = async (invoiceId) => {
        try {
            message.loading({ content: 'Cargando productos al carrito...', key: 'loadCart', duration: 0 });
            
            // Fetch complete invoice data from backend
            const response = await getInvoice(invoiceId);
            
            if (!response || !response.success) {
                throw new Error('No se pudieron obtener los datos de la factura');
            }
            
            const invoiceData = response.data;
            const items = invoiceData.items || [];
            
            if (items.length === 0) {
                message.warning({ content: 'Esta factura no tiene productos', key: 'loadCart' });
                return;
            }
            
            // Clear current cart first
            clearCart();
            
            // Add each product to cart with its quantity, splitter, and volume pricing state
            let addedCount = 0;
            for (const item of items) {
                // Only add items with valid ProductId
                if (item.product_id) {
                    // First add the item to cart (this creates it with quantity 1)
                    toggleItemInCart(item.product_id, true);
                    
                    // Then update the quantity if different from 1
                    const quantity = parseFloat(item.cantidad) || 1;
                    if (quantity !== 1) {
                        updateItemQuantity(item.product_id, quantity);
                    }
                    
                    // Update splitter if present and different from 1
                    const splitter = parseInt(item.splitter) || 1;
                    if (splitter !== 1) {
                        updateItemSplitter(item.product_id, splitter);
                    }
                    
                    // Activate volume pricing if it was active on the original invoice
                    // Backend stores this as is_volume_pricing_active (snake_case)
                    const isVolumePricingActive = item.is_volume_pricing_active === true || item.is_volume_pricing_active === 1;
                    if (isVolumePricingActive) {
                        toggleVolumePrice(item.product_id);
                        console.log(`[InvoicesPage] Volume pricing activated for product ${item.product_id}`);
                    }
                    
                    addedCount++;
                }
            }
            
            message.success({ 
                content: `${addedCount} producto${addedCount !== 1 ? 's' : ''} cargado${addedCount !== 1 ? 's' : ''} al carrito`, 
                key: 'loadCart',
                duration: 3
            });
            
        } catch (error) {
            console.error('Error loading invoice to cart:', error);
            message.error({ 
                content: `Error al cargar productos: ${error.message}`, 
                key: 'loadCart',
                duration: 5
            });
        }
    };

    // View credit notes for an invoice
    const handleViewCreditNotes = async (invoiceId) => {
        const creditNotes = invoiceToCreditNotesMap[invoiceId.toString()] || [];
        
        if (creditNotes.length === 1) {
            // If only one credit note, fetch and open it directly
            const creditNote = creditNotes[0];
            try {
                message.loading({ content: 'Cargando nota de crédito...', key: 'loadCreditNote', duration: 0 });
                
                // Fetch full credit note data with items from backend
                const response = await getCreditNote(creditNote.id);
                
                if (response && response.success && response.creditNote) {
                    // Store complete credit note data in sessionStorage for print page
                    sessionStorage.setItem('printCreditNoteData', JSON.stringify(response.creditNote));
                    message.success({ content: 'Nota de crédito cargada', key: 'loadCreditNote', duration: 1 });
                    // Open credit note in print-quote page
                    window.open(`#/print-quote?creditNoteId=${creditNote.id}`, '_blank');
                } else {
                    // If API fails, store the basic credit note data as fallback
                    sessionStorage.setItem('printCreditNoteData', JSON.stringify(creditNote));
                    message.warning({ content: 'Mostrando datos básicos de la nota de crédito', key: 'loadCreditNote', duration: 2 });
                    // Open credit note in print-quote page anyway
                    window.open(`#/print-quote?creditNoteId=${creditNote.id}`, '_blank');
                }
            } catch (error) {
                console.error('Error loading credit note:', error);
                // Store the basic data as fallback
                sessionStorage.setItem('printCreditNoteData', JSON.stringify(creditNote));
                message.warning({ content: 'Mostrando datos básicos de la nota de crédito', key: 'loadCreditNote', duration: 2 });
                // Open credit note in print-quote page anyway
                window.open(`#/print-quote?creditNoteId=${creditNote.id}`, '_blank');
            }
        } else if (creditNotes.length > 1) {
            // If multiple credit notes, show a modal to choose
            Modal.info({
                title: 'Notas de Crédito',
                width: 600,
                content: (
                    <div>
                        <p>Esta factura tiene {creditNotes.length} notas de crédito:</p>
                        <ul>
                            {creditNotes.map(cn => (
                                <li key={cn.id} style={{ marginBottom: 8 }}>
                                    <a 
                                        href="#"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                                message.loading({ content: 'Cargando nota de crédito...', key: 'loadCreditNote', duration: 0 });
                                                
                                                // Fetch full credit note data
                                                const response = await getCreditNote(cn.id);
                                                
                                                if (response && response.success && response.creditNote) {
                                                    sessionStorage.setItem('printCreditNoteData', JSON.stringify(response.creditNote));
                                                    message.success({ content: 'Nota de crédito cargada', key: 'loadCreditNote', duration: 1 });
                                                } else {
                                                    sessionStorage.setItem('printCreditNoteData', JSON.stringify(cn));
                                                    message.warning({ content: 'Mostrando datos básicos', key: 'loadCreditNote', duration: 2 });
                                                }
                                                
                                                window.open(`#/print-quote?creditNoteId=${cn.id}`, '_blank');
                                            } catch (error) {
                                                console.error('Error loading credit note:', error);
                                                sessionStorage.setItem('printCreditNoteData', JSON.stringify(cn));
                                                message.warning({ content: 'Mostrando datos básicos', key: 'loadCreditNote', duration: 2 });
                                                window.open(`#/print-quote?creditNoteId=${cn.id}`, '_blank');
                                            }
                                        }}
                                        style={{ marginRight: 8 }}
                                    >
                                        {cn.numero_comprobante || `NC #${cn.id}`}
                                    </a>
                                    - {cn.cancellation_type === 'full' ? 'Completa' : 'Parcial'}
                                    {' - '}{formatPrice(cn.total)}
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
                okText: 'Cerrar'
            });
        }
    };

    // Handle credit note creation
    const handleCreateCreditNote = (record) => {
        Modal.confirm({
            title: '¿Crear Nota de Crédito?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Se creará una Nota de Crédito para anular este comprobante:</p>
                    <p><strong>Tipo:</strong> {record.status === 'PRESUPUESTO' ? 'Presupuesto' : 'Factura'}</p>
                    <p><strong>Número:</strong> {record.presupuesto_numero || record.numero_comprobante || 'N/A'}</p>
                    <p><strong>Cliente:</strong> {record.cliente_razon_social || 'Consumidor Final'}</p>
                    <p><strong>Monto:</strong> {formatPrice(record.total)}</p>
                    <br />
                    <p style={{ color: '#ff4d4f' }}>
                        <strong>Importante:</strong> Esta acción es irreversible y se registrará en AFIP.
                    </p>
                </div>
            ),
            okText: 'Crear Nota de Crédito',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    message.loading({ content: 'Creando Nota de Crédito...', key: 'creditNote', duration: 0 });
                    
                    // Call credit note service - backend will handle all data gathering
                    const response = await createCreditNote(record.id, {
                        reason: 'Anulación de comprobante',
                        cancellationType: 'full'
                    });
                    
                    if (response.success) {
                        const creditNoteNumber = response.data?.numeroComprobante || response.creditNote?.numero_comprobante || 'N/A';
                        const stockRestoredCount = response.creditNote?.stockRestoredCount || 0;
                        const stockMsg = stockRestoredCount > 0 ? ` Stock restaurado: ${stockRestoredCount} producto${stockRestoredCount > 1 ? 's' : ''}.` : '';
                        message.success({
                            content: `Nota de Crédito creada: ${creditNoteNumber}.${stockMsg}`,
                            key: 'creditNote',
                            duration: 5
                        });
                        
                        // Refresh invoice list
                        fetchInvoices(pagination.current, pagination.pageSize);
                    } else {
                        throw new Error(response.message || 'Error al crear Nota de Crédito');
                    }
                } catch (error) {
                    console.error('Error creating credit note:', error);
                    message.error({
                        content: `Error al crear Nota de Crédito: ${error.message}`,
                        key: 'creditNote',
                        duration: 7
                    });
                }
            }
        });
    };

    // Handle partial credit note
    const handlePartialCreditNote = (record) => {
        setSelectedInvoiceForPartialCredit(record);
        setPartialCreditModalVisible(true);
    };

    // Handle partial credit modal close
    const handlePartialCreditModalClose = () => {
        setPartialCreditModalVisible(false);
        setSelectedInvoiceForPartialCredit(null);
    };

    // Handle partial credit success
    const handlePartialCreditSuccess = () => {
        setPartialCreditModalVisible(false);
        setSelectedInvoiceForPartialCredit(null);
        // Refresh invoice list
        fetchInvoices(pagination.current, pagination.pageSize);
    };

    // Check if credit note can be created for invoice
    const canCreateCreditNote = (record) => {
        // Only allow credit notes for approved/completed invoices/presupuestos
        // Don't allow for:
        // - Already existing credit notes
        // - Canceled/cancelled invoices (already have a credit note)
        // - Invoices that have a FULL approved credit note
        const isCreditNote = record.tipo_comprobante?.includes('NOTA DE CREDITO') || 
                            record.presupuesto_numero?.includes('NC');
        const isCanceled = record.status === 'canceled' || record.status === 'cancelled';
        const hasFullCreditNote = invoicesWithFullCreditNotes.has(record.id.toString());
        
        // Allow for approved, completed (same as approved), or PRESUPUESTO status
        const isValidStatus = record.status === 'approved' || 
                             record.status === 'completed' || 
                             record.status === 'PRESUPUESTO';
        
        // Allow partial credit notes even if partial credit notes already exist
        // Only block if there's a FULL credit note
        return isValidStatus && 
               !isCreditNote && 
               !isCanceled && 
               !hasFullCreditNote;
    };

    // Format price
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusColors = {
            'PRESUPUESTO': 'blue',
            'draft': 'gold', // Queued invoices - warning color
            'approved': 'green',
            'completed': 'green', // Same color as approved
            'error': 'red',
            'failed': 'red',
            'pending': 'orange',
            'processing': 'gold',
            'canceled': 'red',
            'cancelled': 'red',
        };
        return statusColors[status] || 'default';
    };

    // Table columns
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Tipo',
            key: 'tipo',
            width: 120,
            render: (_, record) => {
                // Get tipo_comprobante from record
                const tipoComprobante = record.tipo_comprobante || '';
                
                // Determine document type and color
                if (tipoComprobante.includes('NOTA DE CREDITO') || tipoComprobante.includes('NC')) {
                    return <Tag color="red">Nota de Crédito</Tag>;
                }
                
                if (tipoComprobante === 'PRESUPUESTO' || record.status === 'PRESUPUESTO') {
                    return <Tag color="blue">Factura X</Tag>;
                }
                
                if (tipoComprobante === 'FACTURA A' || tipoComprobante === 'A') {
                    return <Tag color="green">Factura A</Tag>;
                }
                
                if (tipoComprobante === 'FACTURA B' || tipoComprobante === 'B') {
                    return <Tag color="orange">Factura B</Tag>;
                }
                
                if (tipoComprobante === 'FACTURA C' || tipoComprobante === 'C') {
                    return <Tag color="cyan">Factura C</Tag>;
                }
                
                // Fallback for generic factura without specific type
                if (tipoComprobante.includes('FACTURA')) {
                    return <Tag color="green">Factura</Tag>;
                }
                
                // Default fallback
                return <Tag color="default">{tipoComprobante || 'Sin especificar'}</Tag>;
            },
        },
        {
            title: 'Número',
            key: 'numero',
            width: 200,
            render: (_, record) => {
                // For PRESUPUESTO: Show presupuesto_numero
                if (record.status === 'PRESUPUESTO' && record.presupuesto_numero) {
                    return <span>{record.presupuesto_numero}</span>;
                }
                
                // For FACTURA: Check multiple possible field names
                const numero = record.invoice_number || 
                              record.numero_comprobante || 
                              record.numero ||
                              (record.punto_venta && record.numero_factura ? 
                                `${record.punto_venta}-${String(record.numero_factura).padStart(8, '0')}` : null);
                
                // If no invoice number but has CAE, show CAE as reference
                if (!numero && record.cae && record.cae.trim()) {
                    return (
                        <Tooltip title={`CAE: ${record.cae.trim()}`}>
                            <span style={{ color: '#1890ff', cursor: 'help' }}>
                                CAE: {record.cae.trim().substring(0, 10)}...
                            </span>
                        </Tooltip>
                    );
                }
                
                return <span>{numero || 'N/A'}</span>;
            },
        },
        {
            title: 'Ref. Externa',
            dataIndex: 'external_reference',
            key: 'external_reference',
            ellipsis: true,
        },
        {
            title: 'Cliente',
            dataIndex: 'cliente_razon_social',
            key: 'cliente_razon_social',
            ellipsis: true,
            width: 200,
            render: (text) => text || 'Consumidor Final',
        },
        {
            title: 'Método de Pago',
            dataIndex: 'payment_method',
            key: 'payment_method',
            width: 150,
            render: (method) => {
                if (!method) return '-';
                
                // Map TusFacturas payment method codes (200-based) to display names
                const paymentMethodMap = {
                    // TusFacturas payment codes
                    '201': 'Contado',
                    '202': 'Tarjeta de Débito',
                    '203': 'Tarjeta de Crédito',
                    '204': 'Cuenta Corriente',
                    '205': 'Cheque',
                    '206': 'Transferencia Bancaria',
                    '207': 'Otros medios de pago electrónico',
                    '208': 'Otra',
                    
                    // Also support numeric values without quotes
                    201: 'Contado',
                    202: 'Tarjeta de Débito',
                    203: 'Tarjeta de Crédito',
                    204: 'Cuenta Corriente',
                    205: 'Cheque',
                    206: 'Transferencia Bancaria',
                    207: 'Otros medios de pago electrónico',
                    208: 'Otra',
                };
                
                // Convert to string and look up
                return paymentMethodMap[method] || paymentMethodMap[String(method)] || method;
            },
        },
        {
            title: 'Fecha Emisión',
            dataIndex: 'fecha_emision',
            key: 'fecha_emision',
            width: 140,
            render: (date, record) => {
                if (!date) return 'N/A';
                // Parse date without timezone conversion
                const dateStr = String(date).split('T')[0]; // Get date part only
                const [year, month, day] = dateStr.split('-');
                const dateFormatted = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                
                // Get time from created_at if available
                if (record.created_at) {
                    const createdDate = new Date(record.created_at);
                    const hours = String(createdDate.getHours()).padStart(2, '0');
                    const minutes = String(createdDate.getMinutes()).padStart(2, '0');
                    return `${dateFormatted} ${hours}:${minutes}`;
                }
                return dateFormatted;
            },
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 160,
            render: (status, record) => {
                // Check if this invoice has credit notes
                const hasPartialCreditNote = invoicesWithPartialCreditNotes.has(record.id.toString());
                const hasFullCreditNote = invoicesWithFullCreditNotes.has(record.id.toString());
                
                if (hasFullCreditNote) {
                    // Full credit note - invoice is completely cancelled
                    return (
                        <Tag color="volcano">
                            Cancelación Completa
                        </Tag>
                    );
                } else if (hasPartialCreditNote) {
                    // Partial credit note - invoice is partially credited
                    return (
                        <Tag color="orange">
                            Cancelación Parcial
                        </Tag>
                    );
                }
                
                // Regular status badges
                const statusLabels = {
                    'PRESUPUESTO': 'PROFORMA',
                    'draft': 'En Cola',
                    'approved': 'Aprobado',
                    'completed': 'Aprobado', // Treat completed as approved
                    'error': 'Error',
                    'failed': 'Fallo/No Procesada',
                    'pending': 'Pendiente',
                    'processing': 'Procesando',
                    'canceled': 'Cancelada',
                    'cancelled': 'Cancelada',
                };
                const label = statusLabels[status] || status;
                return <Tag color={getStatusColor(status)}>{label}</Tag>;
            },
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => {
                // Check if this invoice has credit notes
                const hasCreditNotes = invoicesWithCreditNotes.has(record.id.toString());
                const creditNotes = invoiceToCreditNotesMap[record.id.toString()] || [];
                
                return (
                    <Space size="small">
                        <Tooltip title="Ver detalles">
                            <Button
                                type="primary"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Vista previa de Factura en PDF con CAE y datos del comprobante'); return; }
                                    handleViewInvoice(record.id);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Cargar en Carrito">
                            <Button
                                type="default"
                                size="small"
                                icon={<ShoppingCartOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Productos de la factura disponibles para reutilizar en una nueva venta'); return; }
                                    handleLoadToCart(record.id);
                                }}
                            />
                        </Tooltip>
                        {hasCreditNotes && (
                            <Tooltip title={`Ver ${creditNotes.length > 1 ? 'Notas de Crédito' : 'Nota de Crédito'}`}>
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<CreditCardOutlined />}
                                    onClick={() => {
                                        if (isDemoMode) { message.info('Nota de Crédito asociada a esta factura'); return; }
                                        handleViewCreditNotes(record.id);
                                    }}
                                    style={{ color: '#722ed1', borderColor: '#722ed1' }}
                                />
                            </Tooltip>
                        )}
                        {canCreateCreditNote(record) && (
                            <>
                                <Tooltip title="Nota de Crédito Parcial">
                                    <Button
                                        type="default"
                                        size="small"
                                        icon={
                                            <span style={{ position: 'relative', display: 'inline-block' }}>
                                                <UnorderedListOutlined />
                                                <CloseOutlined style={{ fontSize: '10px', position: 'absolute', top: '-2px', right: '-6px' }} />
                                            </span>
                                        }
                                        onClick={() => {
                                            if (isDemoMode) { message.info('Emisión de Nota de Crédito Parcial — anulación de ítems seleccionados'); return; }
                                            handlePartialCreditNote(record);
                                        }}
                                        style={{ color: '#ff7875', borderColor: '#ff7875' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Nota de Crédito Completa">
                                    <Button
                                        type="default"
                                        size="small"
                                        icon={<CloseSquareOutlined />}
                                        onClick={() => {
                                            if (isDemoMode) { message.warning('Emisión de Nota de Crédito Completa — anulación total del comprobante'); return; }
                                            handleCreateCreditNote(record);
                                        }}
                                        danger
                                    />
                                </Tooltip>
                            </>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="invoices-page-container">
            <div className="invoices-header">
                <h1>
                    <ShopOutlined />
                    Facturas y Proformas
                </h1>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchInvoices(pagination.current, pagination.pageSize)}
                >
                    Actualizar
                </Button>
            </div>

            {/* Summary Cards — admin only */}
            {isAdmin && <div className="invoices-summary">
                <Row gutter={16} style={{ marginBottom: 24, display: 'flex' }}>
                    <Col xs={24} sm={12} md={5} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #1890ff', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>TOTAL FACTURAS</Typography.Text>}
                                        value={summary.currentMonth.count}
                                        valueStyle={{ color: '#1890ff', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Facturas A: <strong>{summary.currentMonth.countByType.facturaA}</strong> | B: <strong>{summary.currentMonth.countByType.facturaB}</strong> | X: <strong>{summary.currentMonth.countByType.presupuesto}</strong>
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={24} md={9} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #722ed1', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: 24 }}>
                                    {/* Left column: main stat */}
                                    <div style={{ flex: 1 }}>
                                        <Statistic
                                            title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>MES ACTUAL</Typography.Text>}
                                            value={Math.ceil(summary.currentMonth.totalSum)}
                                            valueStyle={{ color: '#722ed1', fontSize: '48px' }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                            Total de <strong>{summary.currentMonth.count}</strong> Facturas
                                        </div>
                                    </div>
                                    {/* Right column: deductions breakdown */}
                                    {summary.currentMonth.deductions && (() => {
                                        const { canceled = 0, failed = 0, creditNoted = 0 } = summary.currentMonth.deductions;
                                        const totalAnuladas = canceled + creditNoted;
                                        const totalDeductions = totalAnuladas + failed;
                                        const realizedRevenue = summary.currentMonth.totalSum - totalDeductions;
                                        const fmt = (n) => Math.ceil(n).toLocaleString('es-AR');
                                        return (
                                            <div style={{ flex: 1, borderLeft: '1px solid #e8e8e8', paddingLeft: 16, display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '13px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999', marginBottom: 4 }}>
                                                    <span>(-) Fallidas</span>
                                                    <span style={{ color: '#cf1322', fontWeight: 600 }}>-${fmt(failed)}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999', marginBottom: 4 }}>
                                                    <Tooltip title="Facturas A, B y X anuladas o con nota de crédito activa">
                                                        <span style={{ cursor: 'default', borderBottom: '1px dashed #ccc' }}>(-) Anuladas NC A/B/X</span>
                                                    </Tooltip>
                                                    <span style={{ color: '#cf1322', fontWeight: 600 }}>-${fmt(totalAnuladas)}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid #e8e8e8', marginTop: 6, paddingTop: 8, color: '#722ed1', fontSize: '15px' }}>
                                                    <Tooltip title="Coincide con el Análisis de Rentabilidad del mismo período">
                                                        <span style={{ cursor: 'default', borderBottom: '1px dashed #52c41a' }}>Ingreso realizado</span>
                                                    </Tooltip>
                                                    <span>${fmt(realizedRevenue)}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={5} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #52c41a', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>FACTURAS APROBADAS</Typography.Text>}
                                        value={summary.currentMonth.approvedCount + summary.currentMonth.presupuestoCount}
                                        valueStyle={{ color: '#52c41a', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        A/B: <strong>{summary.currentMonth.approvedCount}</strong> | X: <strong>{summary.currentMonth.presupuestoCount}</strong>
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={5} style={{ display: 'flex' }}>
                        <Card size="small" style={{ borderLeft: '4px solid #cf1322', width: '100%' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <>
                                    <Statistic
                                        title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>ANULADAS (MES ACTUAL)</Typography.Text>}
                                        value={summary.currentMonth.canceledCount}
                                        valueStyle={{ color: '#cf1322', fontSize: '48px' }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '13px', color: '#666' }}>
                                        Completas: <strong>{summary.currentMonth.fullCanceledCount}</strong> | Parciales: <strong>{summary.currentMonth.partialCanceledCount}</strong>
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>}

            {/* Filters */}
            <div className="invoices-filters">
                <Space wrap size="middle">
                    <Select
                        placeholder="Filtrar por tipo"
                        style={{ width: 150 }}
                        allowClear
                        value={filters.tipoComprobante}
                        onChange={(value) => handleFilterChangeWithReload('tipoComprobante', value)}
                    >
                        <Option value="FACTURA A">Factura A</Option>
                        <Option value="FACTURA B">Factura B</Option>
                        <Option value="PRESUPUESTO">Factura X</Option>
                    </Select>

                    <Select
                        placeholder="Filtrar por estado"
                        style={{ width: 200 }}
                        allowClear
                        value={filters.status}
                        onChange={(value) => handleFilterChangeWithReload('status', value)}
                    >
                        <Option value="PRESUPUESTO">Proforma</Option>
                        <Option value="approved">Aprobado</Option>
                        <Option value="error">Error</Option>
                        <Option value="pending">Pendiente</Option>
                        <Option value="cancelacion_completa">Cancelación Completa</Option>
                        <Option value="cancelacion_parcial">Cancelación Parcial</Option>
                    </Select>

                    <Input
                        placeholder="Buscar por número"
                        style={{ width: 160 }}
                        allowClear
                        value={filters.numeroComprobante}
                        onChange={(e) => handleFilterChangeWithReload('numeroComprobante', e.target.value || null)}
                        onKeyPress={handleKeyPress}
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        title="Busca en los últimos 5 dígitos del número de comprobante"
                    />

                    <Input
                        placeholder="Buscar por cliente"
                        style={{ width: 200 }}
                        allowClear
                        value={filters.clienteRazonSocial}
                        onChange={(e) => handleFilterChangeWithReload('clienteRazonSocial', e.target.value || null)}
                        onKeyPress={handleKeyPress}
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        title="Busca por razón social del cliente"
                    />

                    <ConfigProvider locale={locale}>
                        <RangePicker
                            format="DD/MM/YYYY"
                            placeholder={['Fecha desde', 'Fecha hasta']}
                            disabledDate={disabledDate}
                            onChange={(dates) => {
                                if (dates) {
                                    handleFilterChange('dateFrom', dates[0].format('YYYY-MM-DD'));
                                    handleFilterChange('dateTo', dates[1].format('YYYY-MM-DD'));
                                } else {
                                    handleFilterChange('dateFrom', null);
                                    handleFilterChange('dateTo', null);
                                }
                            }}
                        />
                    </ConfigProvider>

                    <Button type="primary" onClick={handleApplyFilters}>
                        Aplicar Filtros
                    </Button>
                    <Button onClick={handleClearFilters}>
                        Limpiar
                    </Button>
                </Space>
            </div>

            {/* Table */}
            <div className="invoices-table-container">
                <Table
                    columns={columns}
                    dataSource={invoices}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        locale: {
                            items_per_page: '/ pág.',
                        },
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    size="middle"
                />
            </div>

            {/* Partial Credit Modal */}
            {partialCreditModalVisible && selectedInvoiceForPartialCredit && (
                <PartialCreditModal
                    visible={partialCreditModalVisible}
                    invoice={selectedInvoiceForPartialCredit}
                    onCancel={handlePartialCreditModalClose}
                    onSuccess={handlePartialCreditSuccess}
                />
            )}
        </div>
    );
};

export default InvoicesPage;
